<template>
  <div class="subscriber-avatar-editor">
    <input
      class="d-none"
      ref="input"
      type="file"
      accept="image/*;capture=camera"
      @input="e => handleUpload(e)"
    />

    <div class="subscriber-avatar-editor__container">
      <v-button
        class="subscriber-avatar-editor__edit"
        small
        round
        light
        @click="handleEdit"
      >
        <v-icon icon="photo_camera" />
      </v-button>

      <subscriber-avatar :subscriber="user" font-size="2rem" />
    </div>

    <v-modal
      modal-class="subscriber-avatar-editor__modal"
      short
      :disable-close="isSaving"
    >
      <template :slot="USER_IMAGE_HASH">
        <header class="p-2 border-bottom mb-4">
          <h3 class="my-0">
            {{ $t("components.subscriberAvatarEditor.profilePhoto") }}
          </h3>
        </header>

        <div class="subscriber-avatar-editor__container mx-auto p-relative">
          <v-button
            v-if="tmpUser.avatarUrls"
            class="subscriber-avatar-editor__modal-remove"
            small
            round
            secondary
            :disabled="isSaving"
            @click="removeFile"
          >
            <v-icon icon="close" />
          </v-button>

          <subscriber-avatar :subscriber="tmpUser" font-size="4rem" />
        </div>

        <div
          v-if="errorMessage"
          class="subscriber-avatar-editor__error-message mt-2 text-center text-small text-red"
        >
          {{ errorMessage }}
        </div>

        <cta-row white-bg overlay-modal row>
          <v-button ghost class="w-50" :disabled="isSaving" @click="chooseFile">
            {{ $t("components.subscriberAvatarEditor.selectAnother") }}
          </v-button>

          <v-button
            primary
            class="w-50"
            @click="saveFile"
            :is-loading="isSaving"
          >
            {{ $t("components.subscriberAvatarEditor.save") }}
          </v-button>
        </cta-row>
      </template>
    </v-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex"
import { isPresent } from "@/lib/utils"

import CtaRow from "@/components/CtaRow"
import SubscriberAvatar from "@/components/SubscriberAvatar"
import ImageUpload from "@/lib/mixins/image-upload"

const USER_IMAGE_HASH = "#user-avatar"

export default {
  mixins: [ImageUpload],

  components: {
    CtaRow,
    SubscriberAvatar
  },

  props: {
    user: { type: Object, required: true }
  },

  data() {
    return {
      errorMessage: "",
      isModalOpen: false,
      file: null,
      sizes: this.user.imageSize,
      USER_IMAGE_HASH
    }
  },

  computed: {
    tmpUser() {
      let avatarUrls = null
      if (this.file) {
        const url = this.blobUrl(this.file)
        avatarUrls = {
          xs: url,
          sm: url,
          original: url
        }
      } else if (this.sizes) {
        avatarUrls = this.sizes
      }

      return { ...this.user, avatarUrls }
    }
  },

  methods: {
    isPresent,
    ...mapActions("auth", ["updateAvatar"]),

    handleEdit() {
      if (this.user.avatarUrls) {
        this.sizes = this.user.avatarUrls
        this.openModal()
      } else {
        this.chooseFile()
      }
    },

    chooseFile() {
      this.$refs.input.click()
    },

    removeFile() {
      this.file = null
      this.sizes = null
      this.errorMessage = ""
    },

    reset() {
      this.file = null
      this.$refs.input.value = ""
      this.errorMessage = ""
    },

    close() {
      this.reset()
      this.$router.removeHash()
    },

    openModal() {
      this.$router.push(USER_IMAGE_HASH)
    },

    handleUpload(e) {
      this.addFile(e.target.files[0])
    },

    addFile(file) {
      this.reset()

      this.openModal()

      const validation = this.validateImage(file)

      if (!validation.valid) {
        this.showMessage(validation.errorMessage)

        return
      }

      this.openModal()
      this.file = file
    },

    async saveFile() {
      if (this.file) {
        try {
          const blob = await this.uploadImage(this.file)
          await this.updateAvatar({ avatar: blob.signed_id })

          this.close()
        } finally {
          this.isSaving = false
        }
      } else if (!this.sizes) {
        this.isSaving = true
        this.updateAvatar({ image: null })
          .then(() => {
            this.close()
          })
          .finally(() => (this.isSaving = false))
      } else {
        this.close()
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.subscriber-avatar-editor__container {
  width: 5rem;
  height: 5rem;
  position: relative;
  margin-left: auto;
  margin-right: auto;
}

.subscriber-avatar-editor__edit {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(10%, -10%);
  z-index: 1;
}

.subscriber-avatar-editor__modal {
  .subscriber-avatar-editor__container {
    width: 10rem;
    height: 10rem;
  }
}

.subscriber-avatar-editor__modal-remove {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(-0.2rem, 1rem);
  z-index: 1;
}
</style>
